/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { WarningOutlined, LoadingOutlined } from '@ant-design/icons'
import { Alert, Modal } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { useCallback, useMemo, useState } from 'react'
import Text from '../Text'
import { SwitchInput } from '../SwitchInput'
import useWindowSize from 'src/hooks/useWindowSize'
import { isCanlan } from '@sportninja/common/utils/customer-name'

interface CancelRegistrationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (refundAllFunds: boolean) => void
  entityName: string
  entityType: 'team' | 'player'
  isLoading?: boolean
  error?: string | null
}

/**
 * Confirmation modal for cancelling team or player registrations
 * Follows the same design patterns as DeleteConfirmationModal
 */
const CancelRegistrationModal = ({
  isOpen,
  onClose,
  onConfirm,
  entityName,
  entityType,
  isLoading = false,
  error = null,
}: CancelRegistrationModalProps) => {
  console.log('🔄 CancelRegistrationModal: Rendering modal', {
    isOpen,
    entityName,
    entityType,
    isLoading,
  })

  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const [refundAllFunds, setRefundAllFunds] = useState(false)

  /**
   * Handles modal close and resets state
   */
  const handleClose = useCallback(() => {
    if (isLoading) return // Prevent closing while loading
    onClose()
  }, [onClose, isLoading])

  /**
   * Handles confirmation action
   */
  const handleConfirm = useCallback(() => {
    if (isLoading) return // Prevent double submission
    onConfirm(refundAllFunds)
  }, [onConfirm, isLoading, refundAllFunds])

  /**
   * Generates the appropriate message based on entity type
   */
  const getMessage = useCallback(() => {
    return (
      <>
        This action will permanently cancel{' '}
        <span
          css={css`
            font-weight: 700;
          `}
        >
          {entityName}
        </span>{' '}
        registration and remove them from the competition. This action cannot be
        undone.
      </>
    )
  }, [entityName])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '480px'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '16px'};
          display: flex;
          flex-direction: column;
          background: ${colors.SECONDARY_300};
          border: 1px solid ${colors.SECONDARY_100};
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
        }
        .ant-modal-header {
          display: none;
        }
        .ant-modal-close {
          color: ${colors.SOLITUDE};
          font-size: 18px;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        }
      `}
      width={isMobile ? '100%' : 480}
      open={isOpen}
      onCancel={handleClose}
      centered={!isMobile}
      maskClosable={!isLoading}
      footer={null}
      closable={!isLoading}
      styles={{
        content: {
          backgroundColor: colors.SECONDARY_300,
          padding: 0,
        },
        wrapper: {
          zIndex: 10002,
        },
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
          zIndex: 10001,
        },
      }}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          gap: 16px;
          position: relative;
        `}
      >
        {/* Warning Icon */}
        <WarningOutlined
          css={css`
            font-size: 48px;
            color: ${colors.ERROR_200};
          `}
        />

        {/* Title */}
        <Text
          variant='heading1'
          weight='semiBold'
          color={colors.WHITE}
          style={`
            font-family: ${
              isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
            };
            font-size: 36px;
            font-weight: 600;
            line-height: 36px;
            text-align: center;
          `}
        >
          Cancel {entityType === 'team' ? 'Team' : 'Player'} Registration
        </Text>

        {/* Description */}
        <div
          css={css`
            align-self: stretch;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            gap: 8px;
            display: flex;
          `}
        >
          <div
            css={css`
              align-self: stretch;
              text-align: center;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='regular'
              color={colors.WHITE}
            >
              {getMessage()}
            </Text>
          </div>
        </div>

        {/* Refund Toggle */}
        <div
          css={css`
            align-self: stretch;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            gap: 16px;
            padding: 16px;
            border: 1px solid ${colors.SECONDARY_100};
            border-radius: 8px;
            background: rgba(255, 255, 255, 0.05);
            /* Hide for now */
            display: none;
          `}
        >
          <SwitchInput
            title='Refund All Funds'
            subtitle='This will cancel and refund all funds for this registration and any other related to it (in case of team registration)'
            value={refundAllFunds}
            onChange={setRefundAllFunds}
            disabled={isLoading}
            titleColor={colors.WHITE}
            subtitleColor={colors.NEUTRAL_50}
          />
        </div>

        {error && (
          <Alert
            message={error}
            type='error'
            css={css`
              width: 100%;
            `}
          />
        )}

        {/* Divider */}
        <div
          css={css`
            align-self: stretch;
            height: 1px;
            background: ${colors.SECONDARY_100};
          `}
        />

        {/* Action Buttons */}
        <div
          css={css`
            align-self: stretch;
            justify-content: center;
            align-items: center;
            gap: 16px;
            display: flex;
            flex-direction: column;
          `}
        >
          <button
            css={css`
              width: 100%;
              padding: 12px 24px;
              background: ${colors.ERROR_200};
              border-radius: 8px;
              border: 2px solid ${colors.ERROR_200};
              justify-content: center;
              align-items: center;
              gap: 8px;
              display: flex;
              cursor: ${isLoading ? 'not-allowed' : 'pointer'};
              opacity: ${isLoading ? 0.5 : 1};
              transition: all 0.2s;
              &:hover {
                opacity: ${isLoading ? 0.5 : 0.8};
              }
            `}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            <Text
              variant='heading6'
              weight='semiBold'
              color={colors.WHITE}
              style={`
                font-family: ${
                  isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
                };
                font-size: 20px;
                font-weight: 600;
                line-height: 24px;
              `}
            >
              {isLoading && (
                <LoadingOutlined
                  css={css`
                    color: ${colors.WHITE};
                    font-size: 16px;
                    margin-right: 8px;
                  `}
                />
              )}
              {isLoading ? 'CANCELLING...' : 'CANCEL REGISTRATION'}
            </Text>
          </button>
          <button
            css={css`
              width: 100%;
              padding: 12px 24px;
              border-radius: 8px;
              border: 2px solid ${colors.WHITE};
              background: transparent;
              justify-content: center;
              align-items: center;
              gap: 8px;
              display: flex;
              cursor: pointer;
              transition: all 0.2s;
              &:hover {
                background: rgba(255, 255, 255, 0.1);
              }
            `}
            onClick={handleClose}
            disabled={isLoading}
          >
            <Text
              variant='heading6'
              weight='semiBold'
              color={colors.WHITE}
              style={`
                font-family: ${
                  isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'
                };
                font-size: 20px;
                font-weight: 600;
                line-height: 24px;
              `}
            >
              RETURN
            </Text>
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default CancelRegistrationModal
